import { FeatureFlag } from "./UserGroup";

/**
 * Lifecycle state of an organization. INACTIVE mirrors the Wristband tenant
 * being deactivated, which is what actually blocks its users from signing in.
 */
export enum OrganizationStatus {
    ACTIVE = "active",
    INACTIVE = "inactive",
}

export interface Organization {
    id: number;
    name: string;
    domain: string;
    status: OrganizationStatus;
    recordCreationDate: string;
    /** Human members only; the org's bot user is not counted. Absent on the detail endpoint. */
    userCount?: number;
    projectCount?: number;
}

export interface CreateOrganizationRequest {
    orgName: string;
    domain: string;
    /** Feature flags to enable org-wide. */
    featureFlags: FeatureFlag[];
}

/** A member of an organization, as the dashboard lists them. */
export interface OrganizationUser {
    id: number;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    roles: string[];
    status: string;
    invitedAt?: string;
    lastLoginAt?: string;
    recordCreationDate?: string;
}

/** A project as the dashboard lists it; much thinner than ProjectSummary. */
export interface AdminProjectSummary {
    id: number;
    name: string;
    type: string;
    status: string;
    ownerName?: string;
    recordCreationDate: string;
}

/**
 * The organization domain is used verbatim as the Wristband tenant name, so it
 * has to satisfy Wristband's tenant-name rule. The backend enforces the same
 * bounds.
 */
export const ORG_DOMAIN_PATTERN = /^[a-z0-9]+(-+[a-z0-9]+)*$/;
export const ORG_DOMAIN_MIN_LENGTH = 3;
export const ORG_DOMAIN_MAX_LENGTH = 20;

/**
 * Default domain for a new org: the name lower-cased, runs of whitespace turned
 * into hyphens, anything else dropped. Leading and trailing hyphens go too, since
 * ORG_DOMAIN_PATTERN wants the value to start and end on an alphanumeric, and the
 * result is truncated to ORG_DOMAIN_MAX_LENGTH.
 */
export function suggestOrgDomain(orgName: string): string {
    const base = orgName
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
    // Over the limit, cut on a word boundary so the suggestion still reads as
    // words, falling back to a hard cut when the first word alone overruns.
    const boundary = base.lastIndexOf("-", ORG_DOMAIN_MAX_LENGTH);
    const capped =
        base.length <= ORG_DOMAIN_MAX_LENGTH
            ? base
            : base.slice(0, boundary > 0 ? boundary : ORG_DOMAIN_MAX_LENGTH);
    return capped.replace(/^-+|-+$/g, "");
}
